import type { Order } from '../services/orderService';

export function generateOrderReceiptHTML(order: Order, userInfo?: { name?: string; phone?: string; email?: string }, companyInfo?: { name?: string; email?: string; phone?: string }): string {
  const formatDate = (date: Date | string) => {
    if (!date) return 'N/A';
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }) + ' ' + dateObj.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'pending': return '#F59E0B';
      case 'confirmed': return '#3B82F6';
      case 'processing': return '#6366F1';
      case 'ready_for_pickup': return '#8B5CF6';
      case 'ready_for_delivery': return '#A855F7';
      case 'out_for_delivery': return '#D946EF';
      case 'delivered': return '#10B981';
      case 'completed': return '#059669';
      case 'cancelled': return '#EF4444';
      case 'refunded': return '#6B7280';
      default: return '#6B7280';
    }
  };

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return '#10B981';
      case 'pending': return '#F59E0B';
      case 'partially_paid': return '#FB923C';
      case 'failed': return '#EF4444';
      case 'refunded': return '#6B7280';
      case 'cancelled': return '#6B7280';
      default: return '#6B7280';
    }
  };

  const formatStatus = (status: string) => {
    return status.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  // Use provided user info or try to get from localStorage
  const user = userInfo || JSON.parse(localStorage.getItem('auth_user') || '{}');

  return `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Enquiry ${order.orderNumber || order.id} - Receipt</title>
    <style>
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
            line-height: 1.3;
            color: #000;
            background: white;
            font-size: 11px;
        }
        
        .container {
            max-width: 600px;
            margin: 0 auto;
            background: white;
        }
        
        .header {
            background: #1F2937;
            color: white;
            padding: 0.75rem;
            text-align: center;
        }
        
        .header h1 {
            font-size: 1.25rem;
            margin-bottom: 0.125rem;
        }
        
        .header p {
            opacity: 0.9;
            font-size: 0.75rem;
        }
        
        .content {
            padding: 0.75rem;
        }
        
        .section {
            margin-bottom: 0.75rem;
        }
        
        .section-title {
            font-size: 0.875rem;
            font-weight: 600;
            margin-bottom: 0.375rem;
            color: #1F2937;
            border-bottom: 1px solid #E5E7EB;
            padding-bottom: 0.25rem;
        }
        
        .info-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 0.5rem;
            margin-bottom: 0.5rem;
        }
        
        .info-grid-3 {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 0.5rem;
            margin-bottom: 0.5rem;
        }
        
        .info-item {
            padding: 0.125rem 0;
        }
        
        .info-label {
            font-weight: 600;
            color: #4B5563;
            font-size: 0.625rem;
            text-transform: uppercase;
            letter-spacing: 0.025em;
            margin-bottom: 0.125rem;
        }
        
        .info-value {
            color: #1F2937;
            font-size: 0.75rem;
        }
        
        .status-badge {
            display: inline-block;
            padding: 0.125rem 0.375rem;
            border-radius: 9999px;
            font-size: 0.625rem;
            font-weight: 600;
            color: white;
        }
        
        .table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 0.5rem;
            font-size: 0.75rem;
            table-layout: fixed;
        }
        
        .table th {
            background: #F9FAFB;
            padding: 0.25rem 0.5rem;
            text-align: left;
            font-weight: 600;
            color: #6B7280;
            font-size: 0.625rem;
            text-transform: uppercase;
            letter-spacing: 0.025em;
            border-bottom: 1px solid #E5E7EB;
        }
        
        .table th:nth-child(1) {
            width: 50%;
        }
        
        .table th:nth-child(2) {
            width: 15%;
            text-align: center;
        }
        
        .table th:nth-child(3) {
            width: 17.5%;
            text-align: right;
        }
        
        .table th:nth-child(4) {
            width: 17.5%;
            text-align: right;
        }
        
        .table td {
            padding: 0.375rem 0.5rem;
            border-bottom: 1px solid #E5E7EB;
        }
        
        .table td:nth-child(2) {
            text-align: center;
        }
        
        .table td:nth-child(3),
        .table td:nth-child(4) {
            text-align: right;
        }
        
        .table tr:last-child td {
            border-bottom: none;
        }
        
        .text-right {
            text-align: right;
        }
        
        .text-center {
            text-align: center;
        }
        
        .summary {
            margin-top: 0.5rem;
            padding-top: 0.5rem;
            border-top: 1px solid #E5E7EB;
        }
        
        .summary-row {
            display: flex;
            justify-content: space-between;
            padding: 0.125rem 0;
            font-size: 0.75rem;
        }
        
        .summary-row.total {
            font-weight: 700;
            font-size: 0.875rem;
            color: #1F2937;
            border-top: 1px solid #E5E7EB;
            margin-top: 0.25rem;
            padding-top: 0.375rem;
        }
        
        .notes-section {
            background: #F9FAFB;
            padding: 0.5rem;
            border-radius: 0.25rem;
            margin-top: 0.5rem;
            font-size: 0.75rem;
        }
        
        .notes-label {
            font-weight: 600;
            color: #6B7280;
            margin-bottom: 0.25rem;
            font-size: 0.625rem;
        }
        
        .footer {
            background: #F9FAFB;
            padding: 0.75rem;
            text-align: center;
            color: #6B7280;
            font-size: 0.625rem;
        }
        
        @media print {
            body {
                background: white;
                font-size: 10px;
            }
            
            .container {
                box-shadow: none;
                max-width: 100%;
            }
            
            .no-print {
                display: none;
            }
            
            .header {
                padding: 0.5rem;
                background: #1F2937 !important;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
            }
            
            .content {
                padding: 0.5rem;
            }
            
            .section {
                margin-bottom: 0.5rem;
            }
            
            @page {
                margin: 0.5cm;
            }
        }
        
        .print-button {
            position: fixed;
            top: 10px;
            right: 10px;
            background: #3B82F6;
            color: white;
            border: none;
            padding: 0.5rem 1rem;
            border-radius: 0.25rem;
            font-weight: 600;
            cursor: pointer;
            font-size: 0.75rem;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
            transition: background 0.2s;
        }
        
        .print-button:hover {
            background: #2563EB;
        }
        
        /* Compact layout helper classes */
        .compact-row {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 0.125rem 0;
        }
        
        .inline-info {
            display: inline-block;
            margin-right: 1rem;
        }
        
        .inline-info .info-label {
            display: inline;
            margin-right: 0.25rem;
        }
        
        .inline-info .info-value {
            display: inline;
            font-weight: 600;
        }
    </style>
</head>
<body>
    <button class="print-button no-print" onclick="window.print()">Print Receipt</button>
    
    <div class="container">
        <div class="header">
            <h1>${companyInfo?.name || 'Demo Crackers'}</h1>
            <p>Enquiry Receipt</p>
        </div>
        
        <div class="content">
            <!-- Order & Customer Info in one compact section -->
            <div class="section">
                <h2 class="section-title">Enquiry Details</h2>
                <div class="compact-row">
                    <div class="inline-info">
                        <span class="info-label">Enquiry#:</span>
                        <span class="info-value">${order.orderNumber || order.id}</span>
                    </div>
                    <div class="inline-info">
                        <span class="info-label">Date:</span>
                        <span class="info-value">${formatDate(order.createdAt)}</span>
                    </div>
                    <div class="inline-info">
                        <span class="info-label">Status:</span>
                        <span class="status-badge" style="background-color: ${getStatusBadgeColor(order.status)}">
                            ${formatStatus(order.status)}
                        </span>
                    </div>
                    <div class="inline-info">
                        <span class="info-label">Payment:</span>
                        <span class="status-badge" style="background-color: ${getPaymentStatusColor(order.paymentStatus)}">
                            ${formatStatus(order.paymentStatus)}
                        </span>
                    </div>
                </div>
                <div class="compact-row" style="margin-top: 0.375rem;">
                    <div class="inline-info">
                        <span class="info-label">Customer:</span>
                        <span class="info-value">${user.name || 'N/A'}</span>
                    </div>
                    <div class="inline-info">
                        <span class="info-label">Phone:</span>
                        <span class="info-value">${user.phone || 'N/A'}</span>
                    </div>
                   
                </div>
                ${order.shippingAddress ? `
                <div style="margin-top: 0.375rem;">
                    <span class="info-label">Delivery Address:</span>
                    <span class="info-value">
                        ${order.shippingAddress.street}, 
                        ${order.shippingAddress.city}, 
                        ${order.shippingAddress.state} - ${order.shippingAddress.zipCode}
                    </span>
                </div>
                ` : ''}
            </div>
            
            <!-- Order Items -->
            <div class="section">
                <h2 class="section-title">Items</h2>
                <table class="table">
                    <thead>
                        <tr>
                            <th>Item</th>
                            <th>Qty</th>
                            <th>Price</th>
                            <th>Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${order.items.map(item => `
                        <tr>
                            <td>${item.productName}${item.sku ? ` (${item.sku})` : ''}</td>
                            <td>${item.quantity}</td>
                            <td>₹${(item.price || 0).toLocaleString('en-IN')}</td>
                            <td>₹${(item.total || 0).toLocaleString('en-IN')}</td>
                        </tr>
                        `).join('')}
                    </tbody>
                </table>
                
                <!-- Summary in the same row -->
                <div class="summary">
                    <div style="display: flex; justify-content: space-between; align-items: flex-start;">
                        <div style="flex: 1;">
                            ${order.trackingNumber ? `
                            <div>
                                <span class="info-label">Tracking:</span>
                                <span class="info-value">${order.trackingNumber}</span>
                            </div>
                            ` : ''}
                            ${order.notes ? `
                            <div class="notes-section" style="margin-top: 0.5rem; max-width: 250px;">
                                <div class="notes-label">Customer Notes:</div>
                                <div>${order.notes}</div>
                            </div>
                            ` : ''}
                        </div>
                        <div style="min-width: 180px;">
                            <table style="width: 100%; font-size: 0.75rem;">
                                <tr>
                                    <td style="text-align: right; padding: 0.125rem 0;">Subtotal:</td>
                                    <td style="text-align: right; padding: 0.125rem 0 0.125rem 1rem; width: 80px;">₹${(order.subtotal || 0).toLocaleString('en-IN')}</td>
                                </tr>
                                ${order.discount && order.discount > 0 ? `
                                <tr>
                                    <td style="text-align: right; padding: 0.125rem 0;">Discount${order.couponCode ? ` (${order.couponCode})` : ''}:</td>
                                    <td style="text-align: right; padding: 0.125rem 0 0.125rem 1rem;">-₹${order.discount.toLocaleString('en-IN')}</td>
                                </tr>
                                ` : ''}
                                ${order.tax && order.tax > 0 ? `
                                <tr>
                                    <td style="text-align: right; padding: 0.125rem 0;">Tax:</td>
                                    <td style="text-align: right; padding: 0.125rem 0 0.125rem 1rem;">₹${order.tax.toLocaleString('en-IN')}</td>
                                </tr>
                                ` : ''}
                                ${order.shipping && order.shipping > 0 ? `
                                <tr>
                                    <td style="text-align: right; padding: 0.125rem 0;">Shipping:</td>
                                    <td style="text-align: right; padding: 0.125rem 0 0.125rem 1rem;">₹${order.shipping.toLocaleString('en-IN')}</td>
                                </tr>
                                ` : ''}
                                ${order.packing && order.packing > 0 ? `
                                <tr>
                                    <td style="text-align: right; padding: 0.125rem 0;">Packaging:</td>
                                    <td style="text-align: right; padding: 0.125rem 0 0.125rem 1rem;">₹${order.packing.toLocaleString('en-IN')}</td>
                                </tr>
                                ` : ''}
                                <tr style="border-top: 1px solid #E5E7EB;">
                                    <td style="text-align: right; padding: 0.375rem 0 0.125rem 0; font-weight: 700; font-size: 0.875rem;">Total:</td>
                                    <td style="text-align: right; padding: 0.375rem 0 0.125rem 1rem; font-weight: 700; font-size: 0.875rem;">₹${(order.total || 0).toLocaleString('en-IN')}</td>
                                </tr>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            ${order.billingAddress ? `
            <!-- Billing Address -->
            <div class="section">
                <h2 class="section-title">Billing Address</h2>
                <div class="info-value">
                    ${order.billingAddress.street}, 
                    ${order.billingAddress.city}, 
                    ${order.billingAddress.state} - ${order.billingAddress.zipCode}
                </div>
            </div>
            ` : ''}
        </div>
        
        <div class="footer">
            <p><strong>Thank you for your enquiry!</strong> • ${companyInfo?.name || 'Demo Crackers'} - Quality Fireworks • ${companyInfo?.email || 'democrackers@gmail.com'}</p>
        </div>
    </div>
</body>
</html>
  `;
}